import React, { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const inferSocketUrl = () => {
  if (typeof window === 'undefined') return 'http://localhost:5030';
  if (process.env.REACT_APP_API_BASE) {
    return process.env.REACT_APP_API_BASE.replace(/\/api\/?$/, '');
  }
  const host = window.location.hostname.toLowerCase();
  if (host === 'frolickingelfstudio.ca' || host.endsWith('.frolickingelfstudio.ca')) {
    return 'https://api-necro.frolickingelfstudio.ca';
  }
  return process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5030';
};

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || inferSocketUrl();

const formatJoinWindowReason = (reason, gameName) => {
  const label = gameName || 'a game';
  if (reason === 'lobby-seat-change') return `Players have joined ${label}. Want to get in on it?`;
  if (reason === 'round-finished') return `A new round is starting in ${label}. Want to join?`;
  return `There is an open spot in ${label}. Want to get in?`;
};

const normalizeOffer = (payload = {}) => {
  if (!payload?.offerId || !payload?.expiresAt) return null;
  const expiresAtMs = new Date(payload.expiresAt).getTime();
  if (!Number.isFinite(expiresAtMs)) return null;
  return {
    offerId: payload.offerId,
    reason: payload.reason || 'returned-active',
    userId: payload.userId,
    gameId: payload.gameId ? `${payload.gameId}` : '',
    roomCode: payload.roomCode || '',
    gameName: payload.gameName || '',
    createdAt: payload.createdAt || new Date().toISOString(),
    expiresAt: payload.expiresAt,
    expiresAtMs,
  };
};

const WaitlistTopBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const socketRef = useRef(null);
  const [offers, setOffers] = useState([]);
  const [clockTick, setClockTick] = useState(0);
  const isGameRoute = location.pathname.startsWith('/game/');

  const enqueueOffer = useCallback((payload) => {
    const nextOffer = normalizeOffer(payload);
    if (!nextOffer) return;
    if (nextOffer.expiresAtMs <= Date.now()) return;

    setOffers((prev) => {
      const deduped = prev.filter((entry) => entry.offerId !== nextOffer.offerId && entry.expiresAtMs > Date.now());
      const next = [nextOffer, ...deduped];
      next.sort((a, b) => {
        const aCreated = new Date(a.createdAt).getTime() || 0;
        const bCreated = new Date(b.createdAt).getTime() || 0;
        return bCreated - aCreated;
      });
      return next;
    });
  }, []);

  useEffect(() => {
    if (loading || !user?.id) return undefined;

    const token = localStorage.getItem('accessToken');
    if (!token) return undefined;

    const socket = io(`${SOCKET_URL}/game`, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 8,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;
    socket.on('waitlist-join-offer', enqueueOffer);

    return () => {
      socket.off('waitlist-join-offer', enqueueOffer);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [enqueueOffer, loading, user?.id]);

  useEffect(() => {
    if (offers.length === 0) return undefined;
    const timer = setInterval(() => {
      setClockTick((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [offers.length]);

  useEffect(() => {
    if (offers.length === 0) return;
    setOffers((prev) => prev.filter((entry) => entry.expiresAtMs > Date.now()));
  }, [clockTick, offers.length]);

  const activeOffer = offers.length > 0 ? offers[0] : null;
  const secondsRemaining = activeOffer
    ? Math.max(0, Math.ceil((activeOffer.expiresAtMs - Date.now()) / 1000))
    : 0;

  const dismissOffer = useCallback((offerId) => {
    setOffers((prev) => prev.filter((entry) => entry.offerId !== offerId));
  }, []);

  const handleOpenOffer = useCallback(() => {
    if (!activeOffer?.offerId) return;
    const target = activeOffer.gameId
      ? `/game/${activeOffer.gameId}?view=waiting`
      : '/';
    dismissOffer(activeOffer.offerId);
    navigate(target);
  }, [activeOffer, dismissOffer, navigate]);

  if (!activeOffer || secondsRemaining <= 0 || isGameRoute) return null;

  const locationLabel = activeOffer.gameName || (activeOffer.roomCode ? `Room ${activeOffer.roomCode}` : 'a game room');

  return (
    <div className="waitlist-top-banner-shell" role="status" aria-live="polite">
      <div className="waitlist-top-banner">
        <div className="waitlist-top-banner-text">
          <strong>{locationLabel}</strong>
          <span>{formatJoinWindowReason(activeOffer.reason, activeOffer.gameName)}</span>
          <span>{secondsRemaining}s to decide — no reply means no.</span>
        </div>
        <div className="waitlist-top-banner-actions">
          <button className="btn-play" onClick={handleOpenOffer}>Yes, I&apos;m in!</button>
          <button className="btn-cancel" onClick={() => dismissOffer(activeOffer.offerId)}>No thanks</button>
        </div>
      </div>
    </div>
  );
};

export default WaitlistTopBanner;
